import { Component, Input, OnInit } from '@angular/core';

import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import { EnvironmentNames } from '../../../_core/constants/environments';
import FundTypes from '../../../_core/constants/fund-types';
import { Fund } from '../../../_core/models/fund';
import { GrantTransferData } from '../../../_core/models/grantData';
import { Configuration } from '../../../app.constants';

@Component({
  selector: 'app-grant-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  standalone: false,
})
export class GrantSummaryComponent implements OnInit {
  @Input() displayData: GrantTransferData;
  @Input() currency: string;
  @Input() currentFund: Fund;

  fundTypes = FundTypes;
  isSGH = false;
  isGrantFeesCollapsed = true;

  private decimalSymbol = '.';
  private digitGroupingSymbol = ',';
  private digitGrouping = '123,456,789';
  private configDateFormat = 'MM/dd/yyyy';

  constructor(
    private config: Configuration,
    private globalConfigApiService: GlobalConfigApiService
  ) {}

  ngOnInit(): void {
    this.isSGH = this.config.environmentName === EnvironmentNames.SINNGEBER;
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        if (config?.response?.dateFormat) {
          this.configDateFormat = config.response.dateFormat;
        }
        if (config?.response?.numberFormat) {
          this.decimalSymbol = config.response.numberFormat.decimalSymbol?.value || '.';
          this.digitGroupingSymbol = config.response.numberFormat.digitGroupingSymbol?.value || ',';
          this.digitGrouping = config.response.numberFormat.digitGrouping || '123,456,789';
        }
      },
    });
  }

  get totalFees(): number {
    return (
      (this.displayData?.calculatedGrantAssessmentExpense || 0) +
      (this.displayData?.processingAssessmentExpense || 0) +
      (this.displayData?.supplementalGrantAssessment || 0)
    );
  }

  toggleFeesBreakdown(): void {
    this.isGrantFeesCollapsed = !this.isGrantFeesCollapsed;
  }

  formatNumberValue(value: number | string): string {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    const parts = Math.abs(num).toFixed(2).split('.');
    const intPart = parts[0];
    const decPart = parts[1];
    const grouped = this.applyDigitGrouping(intPart);
    const result = grouped + this.decimalSymbol + decPart;
    return num < 0 ? `-${result}` : result;
  }

  private applyDigitGrouping(intPart: string): string {
    const patternParts = this.digitGrouping.replace(/[^0-9,]/g, '').split(',');
    if (patternParts.length <= 1) return intPart;
    const groupSizes = patternParts.map(p => p.length).reverse();
    const digits = intPart.split('').reverse();
    const groups: string[] = [];
    let pos = 0;
    let sizeIdx = 0;
    while (pos < digits.length) {
      const size = groupSizes[Math.min(sizeIdx, groupSizes.length - 1)];
      groups.push(digits.slice(pos, pos + size).reverse().join(''));
      pos += size;
      sizeIdx++;
    }
    return groups.reverse().join(this.digitGroupingSymbol);
  }

  formatDateValue(date: Date | string | number): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return String(date);
    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();
    return this.configDateFormat
      .replace(/yyyy/g, String(year))
      .replace(/yy/g, String(year).slice(-2))
      .replace(/MMM/g, '\x01')
      .replace(/MM/g, String(month).padStart(2, '0'))
      .replace(/M(?!M)/g, String(month))
      .replace(/dd/g, String(day).padStart(2, '0'))
      .replace(/d(?!d)/g, String(day))
      .replace(/\x01/g, ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month - 1]);
  }
}
